import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Info,
  Gift,
  Plus,
  ShoppingBag,
  ChevronLeft,
  Truck,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CarteiraService,
  IndicacaoService,
  type TransacaoResponseDto,
} from "@/services";
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "@/config/base";
import { showToast } from "@/hooks/useToast";

const EXTRATO_PAGE_SIZE = 20;

export const Route = createFileRoute("/app/carteira")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (!token || !user) {
      throw redirect({ to: "/auth/identify" });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const meuCodigoQuery = useQuery({
    queryKey: ["indicacao", "meu-codigo"],
    queryFn: IndicacaoService.getMeuCodigo,
  });

  const saldoQuery = useQuery({
    queryKey: ["carteira", "saldo"],
    queryFn: CarteiraService.getSaldo,
  });

  const extratoQuery = useQuery({
    queryKey: ["carteira", "extrato", 1],
    queryFn: () => CarteiraService.getExtrato(1, EXTRATO_PAGE_SIZE),
  });

  const saldo = saldoQuery.data?.saldo ?? 0;
  const transacoes = extratoQuery.data?.transacoes ?? [];
  const referralCode = meuCodigoQuery.data?.codigo ?? "";

  const referralLink = referralCode
    ? `${window.location.origin}/auth/identify?ref=${referralCode}`
    : "";

  const transacoesVisiveis = showAll ? transacoes : transacoes.slice(0, 3);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(value);

  const handleSaibaMais = async () => {
    if (!referralLink) {
      showToast.error("Código de indicação ainda não disponível.");
      return;
    }

    try {
      await navigator.clipboard.writeText(referralLink);
      showToast.success("Link de indicação copiado!");
      navigate({ to: "/app/perfil/indicacoes" });
    } catch {
      showToast.error("Não foi possível copiar o link de indicação");
    }
  };

  return (
    <div className="min-h-screen bg-background text-slate-900">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="container mx-auto max-w-2xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate({ to: "/app" })}
              className="rounded-full hover:bg-black/5 -ml-2"
            >
              <ChevronLeft className="w-5 h-5 text-primary" />
            </Button>
          </div>
            <h1 className="text-base font-semibold text-slate-900">Minha Carteira</h1>
          <div className="w-8 h-8 flex items-center justify-center">
            <Info className="w-5 h-5 text-slate-400" />
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-2xl px-4 py-4 pb-28 space-y-4">
        <section>
          <div className="bg-white p-5 rounded-2xl border border-black/5 shadow-sm">
            <p className="text-sm text-slate-500 mb-1">Saldo atual</p>
            <div className="flex items-baseline gap-1">
              <span className="text-base font-medium text-slate-700">R$</span>
              <span className="text-4xl font-extrabold text-slate-900 tracking-tight">
                {formatCurrency(saldo).replace("R$", "").trim()}
              </span>
            </div>
            {/* <div className="mt-5 flex gap-3">
              <button
                className="flex-1 bg-primary text-white py-3 rounded-2xl font-semibold text-sm hover:opacity-95 active:scale-[0.99] transition-all"
                onClick={handleAdicionarSaldo}
              >
                Adicionar Saldo
              </button>
              <button
                className="flex-1 border border-slate-200 text-slate-700 py-3 rounded-2xl font-semibold text-sm hover:bg-slate-50 active:scale-[0.99] transition-all"
                onClick={handleRetirarSaldo}
              >
                Retirar
              </button>
            </div> */}
          </div>
        </section>

        {/* <section>
          <h2 className="text-lg font-semibold mb-3">Recarregar</h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              className="bg-white p-4 rounded-2xl border border-black/5 shadow-sm flex flex-col items-center gap-2 hover:bg-slate-50 transition-colors"
              onClick={handlePix}
            >
              <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center">
                <QrCode className="w-5 h-5" />
              </div>
              <span className="font-semibold text-sm">Pix</span>
            </button>
            <button
              className="bg-white p-4 rounded-2xl border border-black/5 shadow-sm flex flex-col items-center gap-2 hover:bg-slate-50 transition-colors"
              onClick={handleCartao}
            >
              <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center">
                <CreditCard className="w-5 h-5" />
              </div>
              <span className="font-semibold text-sm">Cartão</span>
            </button>
          </div>
        </section> */}

        <section>
          <div className="bg-orange-50 p-5 rounded-2xl border border-orange-100 flex items-center justify-between">
            <div className="max-w-[70%]">
              <h3 className="font-bold text-orange-600 text-xl leading-tight">Indique e Ganhe!</h3>
              <p className="text-sm text-slate-700 mt-1">
                Ganhe <span className="font-bold">R$ 5,00</span> por cada amigo que concluir o cadastro.
              </p>
              <button className="mt-3 text-sm font-bold underline text-orange-600" onClick={handleSaibaMais}>
                Saiba mais
              </button>
            </div>
            <div className="w-16 h-16 flex items-center justify-center bg-white rounded-2xl shadow-sm">
              <Gift className="w-9 h-9 text-orange-600" />
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Extrato</h2>
            <button
              className="text-sm font-semibold text-primary"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? "Ver menos" : "Ver tudo"}
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-4 space-y-3">
            {extratoQuery.isLoading && (
              <p className="text-sm text-slate-500">Carregando extrato...</p>
            )}

            {!extratoQuery.isLoading && transacoesVisiveis.length === 0 && (
              <p className="text-sm text-slate-500">Nenhuma transação encontrada.</p>
            )}

            {transacoesVisiveis.map((transacao) => (
              <TransacaoItem key={transacao.id} transacao={transacao} formatCurrency={formatCurrency} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function TransacaoItem({
  transacao,
  formatCurrency,
}: {
  transacao: TransacaoResponseDto;
  formatCurrency: (value: number) => string;
}) {
  const isCredito = transacao.tipo === "credito";
  const isEntrega = transacao.solicitacaoId != null;
  const isEstorno = isCredito && isEntrega;
  const isPagamentoEntrega = !isCredito && isEntrega;

  const icon = isEstorno ? (
    <RotateCcw className="w-5 h-5" />
  ) : isPagamentoEntrega ? (
    <Truck className="w-5 h-5" />
  ) : isCredito ? (
    <Plus className="w-5 h-5" />
  ) : (
    <ShoppingBag className="w-5 h-5" />
  );

  const iconBg = isCredito
    ? "bg-primary/10 text-primary"
    : "bg-gray-100 text-gray-400";

  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-4">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
        <div>
          <p className="font-bold text-sm text-slate-900">{transacao.descricao}</p>
          <p className="text-xs text-slate-500">
            {new Date(transacao.createdAt).toLocaleString("pt-BR", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
      </div>
      <span className={`font-bold text-sm ${isCredito ? "text-emerald-600" : "text-slate-900"}`}>
        {isCredito ? "+ " : "- "}
        {formatCurrency(transacao.valor)}
      </span>
    </div>
  );
}
